/**
 * Catálogo de rutas y resolución de vista.
 *
 * Las dos cosas viven juntas porque la resolución solo puede ser correcta si
 * conoce el catálogo. El módulo no importa React ni toca `window`, así que se
 * puede ejercitar con `node --test` sin navegador — y se hace, en
 * `tests/rutas.test.mjs`.
 *
 * `SEG-1` recordatorio: los permisos de aquí ocultan y redirigen, **no
 * autorizan**. La autorización real la hace el backend en cada caso de uso. Un
 * usuario que escriba la URL a mano y burle esto recibirá un 403 del servidor.
 *
 * Rutas tomadas de INVENTARIO_VISTAS.md §2.
 */
import type { Ubicacion } from './historial.js'

export type Vista =
  | 'acceso'
  | 'primerAcceso'
  | 'panel'
  | 'sorteo'
  | 'bloque'
  | 'expediente'
  | 'documento'
  | 'mesaPartes'
  | 'misOficios'
  | 'oci'
  | 'entidades'
  | 'politica'
  | 'calendario'
  | 'plantillas'
  | 'itemsSeleccion'
  | 'usuarios'
  | 'auditoria'

export type GrupoNavegacion = 'fiscalizacion' | 'circuito' | 'administracion'

export interface DefinicionRuta {
  vista: Vista
  /** Patrón con `:parametro`. El orden en el array decide el desempate. */
  patron: string
  etiqueta: string
  /** Basta con UNO de estos permisos. Vacío = solo exige sesión. */
  permisos: readonly string[]
  /** Ausente = no aparece en el menú lateral (detalle o pantalla sin shell). */
  grupo?: GrupoNavegacion
  /** Fuera del `AppShell`: acceso y primer acceso. */
  sinShell?: boolean
}

export const RUTAS: readonly DefinicionRuta[] = [
  { vista: 'acceso', patron: '/acceso', etiqueta: 'Acceso', permisos: [], sinShell: true },
  { vista: 'primerAcceso', patron: '/acceso/password', etiqueta: 'Primer acceso', permisos: [], sinShell: true },

  { vista: 'panel', patron: '/panel', etiqueta: 'Panel', permisos: ['expediente.ver'], grupo: 'fiscalizacion' },
  {
    vista: 'sorteo',
    patron: '/sorteo',
    etiqueta: 'Selección de muestra',
    permisos: ['padron.cargar', 'sorteo.ejecutar', 'sorteo.confirmar'],
    grupo: 'fiscalizacion',
  },
  // `:periodo` y no `:id`: la URL de un bloque es legible y compartible
  // (`/bloque/2026-09`). La vista resuelve el identificador desde el listado.
  { vista: 'bloque', patron: '/bloque/:periodo', etiqueta: 'Bloque', permisos: ['expediente.ver'] },
  { vista: 'expediente', patron: '/expediente/:id', etiqueta: 'Expediente', permisos: ['expediente.ver'] },
  { vista: 'documento', patron: '/documento/:id', etiqueta: 'Documento declarado', permisos: ['expediente.ver'] },

  {
    vista: 'mesaPartes',
    patron: '/circuito/mesa-partes',
    etiqueta: 'Cola de Mesa de Partes',
    permisos: ['consulta.paso.mesa_partes'],
    grupo: 'circuito',
  },
  {
    vista: 'misOficios',
    patron: '/circuito/mis-oficios',
    etiqueta: 'Mis oficios',
    permisos: ['consulta.paso.notificador'],
    grupo: 'circuito',
  },

  {
    vista: 'oci',
    patron: '/control/oci',
    etiqueta: 'Derivaciones al OCI',
    permisos: ['oci.derivar', 'expediente.ver'],
    grupo: 'fiscalizacion',
  },

  {
    vista: 'entidades',
    patron: '/admin/entidades',
    etiqueta: 'Entidades',
    permisos: ['entidad.administrar'],
    grupo: 'administracion',
  },
  {
    vista: 'politica',
    patron: '/admin/politica',
    etiqueta: 'Política y plazos',
    permisos: ['configuracion.politica', 'configuracion.plazos'],
    grupo: 'administracion',
  },
  {
    vista: 'calendario',
    patron: '/admin/calendario',
    etiqueta: 'Calendario',
    permisos: ['calendario.administrar'],
    grupo: 'administracion',
  },
  {
    vista: 'plantillas',
    patron: '/admin/plantillas',
    etiqueta: 'Plantillas',
    permisos: ['plantillas.administrar'],
    grupo: 'administracion',
  },
  {
    vista: 'itemsSeleccion',
    patron: '/admin/items-seleccion',
    etiqueta: 'Ítems de Selección',
    permisos: ['configuracion.politica'],
    grupo: 'administracion',
  },
  {
    vista: 'usuarios',
    patron: '/admin/usuarios',
    etiqueta: 'Usuarios y permisos',
    permisos: ['usuario.administrar', 'permiso.administrar'],
    grupo: 'administracion',
  },
  {
    vista: 'auditoria',
    patron: '/admin/auditoria',
    etiqueta: 'Auditoría',
    permisos: ['auditoria.ver'],
    grupo: 'administracion',
  },
]

export const ETIQUETA_DE_GRUPO: Readonly<Record<GrupoNavegacion, string>> = {
  fiscalizacion: 'Fiscalización',
  circuito: 'Circuito',
  administracion: 'Administración',
}

/**
 * A qué item del menú pertenece una vista que NO está en el menú.
 *
 * Bloque, expediente y documento son los tres niveles de una misma cosa y se
 * alcanzan navegando, no desde el menú. Sin esta correspondencia el sidebar se
 * queda sin ningún item marcado en cuanto se abre un expediente, y el usuario
 * pierde la referencia de dónde está.
 */
const ITEM_DE_MENU: Partial<Readonly<Record<Vista, Vista>>> = {
  bloque: 'panel',
  expediente: 'panel',
  documento: 'panel',
}

export function itemDeMenuDe(vista: Vista): Vista | undefined {
  return ITEM_DE_MENU[vista]
}

const POR_VISTA = new Map(RUTAS.map((ruta) => [ruta.vista, ruta]))

export function rutaDeVista(vista: Vista): DefinicionRuta {
  const ruta = POR_VISTA.get(vista)
  if (!ruta) throw new Error(`No hay ruta declarada para la vista «${vista}».`)
  return ruta
}

/** Sustituye los `:parametro` del patrón. Codifica: un DNI no, pero un
 *  período o un identificador con caracteres raros sí lo necesitan. */
export function construirRuta(vista: Vista, parametros: Readonly<Record<string, string>> = {}): string {
  return rutaDeVista(vista)
    .patron.split('/')
    .map((segmento) => {
      if (!segmento.startsWith(':')) return segmento
      const valor = parametros[segmento.slice(1)]
      if (valor === undefined) {
        throw new Error(`Falta el parámetro «${segmento.slice(1)}» para la vista «${vista}».`)
      }
      return encodeURIComponent(valor)
    })
    .join('/')
}

export interface RutaResuelta {
  ruta: DefinicionRuta
  parametros: Readonly<Record<string, string>>
}

function emparejar(patron: string, pathname: string): Record<string, string> | undefined {
  const esperados = patron.split('/')
  const recibidos = pathname.replace(/\/+$/, '').split('/')
  if (esperados.length !== recibidos.length) return undefined

  const parametros: Record<string, string> = {}
  for (const [indice, esperado] of esperados.entries()) {
    const recibido = recibidos[indice] ?? ''
    if (esperado.startsWith(':')) {
      if (recibido === '') return undefined
      parametros[esperado.slice(1)] = decodeURIComponent(recibido)
    } else if (esperado !== recibido) {
      return undefined
    }
  }
  return parametros
}

/**
 * Qué vista corresponde a una URL.
 *
 * El primer patrón que empareja gana, y los literales van antes que los
 * paramétricos en el array — por eso `/acceso/password` resuelve a su vista y
 * no queda atrapado por ningún `/:algo`.
 */
export function resolverRuta(pathname: string): RutaResuelta | undefined {
  const limpio = pathname === '' ? '/' : pathname
  for (const ruta of RUTAS) {
    const parametros = emparejar(ruta.patron, limpio)
    if (parametros) return { ruta, parametros }
  }
  return undefined
}

/** Lee un parámetro del query string sin que la vista toque `URLSearchParams`. */
export function parametroDeBusqueda(ubicacion: Ubicacion, nombre: string): string | undefined {
  return new URLSearchParams(ubicacion.search).get(nombre) ?? undefined
}
